// =============================================================================
// Huddler — internal plan builder
// =============================================================================
// CHANGELOG (S50 / recruit gate):
// - Confirm Info now POSTs /api/huddler/resolve-recruit before unlocking the
//   page; failure keeps it locked with the error surfaced. On an email match
//   a persistent mid-green notice strip renders under CompressedInfoBar
//   ("Existing recruit found — proceeding on their file").
// - Invoice mode only: Notes panel below the PlanTermsPanel slot, sent as
//   invoice_note in the /build body (appends to the recruit's admin_notes).
// - /build body carries recruit_id for reference; the server re-resolves by
//   email regardless — no client logic depends on it.
//
// CHANGELOG (S10.2):
// - Title bar center block: now ALWAYS visible (was opacity-0 until sticky).
//   Two-line magazine-masthead layout: top "{firstName}'s CUSTOM RECRUITING
//   PLAN" with italic mid-green name + uppercase white descriptor; bottom
//   small tracked footer with em-dash bookends, "{period} Total · {pill}".
//   Dollar amount keeps the white pill (matches the panel's price-tag visual
//   language); the name is treated as a hero typography element instead.
// - Email validation: hoisted EMAIL_RX + isValidEmail() to module scope so
//   pre-confirm form, EditableField, RecruitCard, ParentCard share one
//   validator. Pre-confirm form now shows per-field touched-state error
//   text below the email input (red border + "That email doesn't look
//   right"). Title bar EditableField shows a wavy red underline on bad
//   email values + a red error strip below the bar that names which field
//   is invalid. canConfirm logic unchanged in semantics.
// - Service card layout: restructured so title + ×N badge + price all share
//   the top row (price right-aligned to card edge). Description spans the
//   full content width below — no longer constrained by the old
//   stepper-column width. Stepper drops to its own row at the bottom when
//   applicable (slot reserved when unchecked to prevent height bounce).
// - Title bar email/phone: text-xs → text-sm.
// - Category tabs: tightened padding/gap to fit "Recruit Development" without
//   truncation (gap-2→gap-1, px-3→px-2, badge px-1.5→px-1).
// - Years×2 toggle slot: now reserves vertical space when in Career mode
//   (invisible + pointer-events-none) so Send button doesn't bounce.
// - Title bar recruit name: light silver border (silver-a/60) + px-2 py-0.5
//   so it pops off the hunter green background instead of getting lost in it.
// - ServiceRow ×N badge: mid-green → black. Sits next to mid-green price on
//   white-bg cards; green-next-to-green was muddy. Plan Summary side keeps
//   mid-green (dark bg, different problem).
// - Plan Summary header: "Custom Recruiting Services" → "Huddler Recruiting
//   Services" (blame the automation for the math).
// - PricingToggle: "Full Career" → "Career". Toggle gets narrower so the
//   floating Custom Total tag has more room when (×2) and big numbers stack.
// - SuccessPanel alignment fix: pulled "Checkout URL" / "Checkout Status"
//   labels OUT of their gray boxes so they align with the heading at the
//   card's content edge (was indented 16px further in than headings/buttons
//   because of the boxes' internal padding). Boxes now hold only the URL
//   string / tracker viz. CheckoutStatusTracker no longer renders its own
//   "Checkout Status" label — caller provides it for consistent alignment.
//
// CHANGELOG (S9.9 / v9.8):
// - Email validation: Confirm Info button requires both recruit and parent
//   email to match X@X.X shape (loose regex, not strict RFC).
// - Tab badge bounce fix: badge slot always rendered, invisible at count=0.
//   Tab height never changes on selection.
// - Send Checkout Link button moved INSIDE Plan Summary card, on the
//   toggles row, right of the Year/Career + ×2 stack. White border.
//   Bottom-centered Send button removed — ONE Send button on the page.
// - Travel disclaimer is now conditional: renders only when the active
//   Plan Builder category contains a requires_travel service. Bolted
//   directly under the white stage card.
// - Plan Summary "Core Recruiting" header → "Core Recruiting Services".
//   Removed the redundant small green "CORE RECRUITING SERVICES" sub-label.
//
// CHANGELOG (S9.7 / v9.6):
// - Required fields for Confirm Info: Recruit first/last/email AND
//   Parent first/last/email. Phones optional.
// - Plan Builder stage: outer transparent wrapper sized to tallest category's
//   natural height, inner card renders at natural height. No bounce, dead
//   space below short cards is acceptable.
// - Category tab transition: combo "header bar wipe" + "card cascade" — the
//   green category title bar slides across to reveal the new content,
//   service cards stagger-fade in behind it (~50ms each).
// - Plan Summary lists (Core Recruiting Services + Custom Recruiting
//   Services) are now SINGLE COLUMN, text-base, same font weight/treatment.
// - White price pills behind every dollar amount: floating Custom Total tag,
//   Core Recruiting price, Custom Recruiting Services subtotal. White bg,
//   mid-green text on the number itself.
// - Ampersand consistency: "Media Training + Podcast" → "Media Training &
//   Podcast" (DB rename in s9_7_migration.sql).
// =============================================================================

import { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import NotificationBell from '../components/NotificationBell.jsx';

const CATEGORY_LABELS = {
  core_recruiting: 'Core Recruiting',
  coach_outreach: 'Coach Outreach',
  social_media: 'Social Media',
  recruit_development: 'Recruit Development',
  media_content: 'Media & Content',
  one_on_one_coaching: '1-on-1 Coaching',
  // DB enum value is `nil_advising` — display label only. See Bible v3.
  nil_advising: 'NIL & Mentorship',
};

const ADDON_CATEGORIES = [
  'coach_outreach',
  'social_media',
  'recruit_development',
  'media_content',
  'one_on_one_coaching',
  'nil_advising',
];

function priceKey(mode) { return mode === 'career' ? 'price_career'    : 'price_year'; }
function qtyKey(mode)   { return mode === 'career' ? 'quantity_career' : 'quantity_year'; }
function maxQtyKey(mode){ return mode === 'career' ? 'max_quantity_career' : 'max_quantity_year'; }
function getBaseQty(svc, mode) { const v = svc && svc[qtyKey(mode)]; return v == null ? 1 : Number(v); }
function getMaxQty(svc, mode)  { const v = svc && svc[maxQtyKey(mode)]; return v == null ? null : Number(v); }
function isLockable(svc) { return !!(svc && svc.quantity_lockable); }

function careerLineTotal(svc, mode, qty) {
  if (mode === 'career' && svc.career_tier1_qty != null) {
    const t1q = Number(svc.career_tier1_qty);
    const t1p = Number(svc.career_tier1_price || 0);
    return t1p + Math.max(0, qty - t1q) * Number(svc.price_career || 0);
  }
  return Number(svc[priceKey(mode)] || 0) * qty;
}

function pathBDiscount(subtotal) {
  if (subtotal < 2000) return { pct: 0, finalPrice: subtotal };
  let pct;
  if (subtotal >= 10000) pct = 0.15;
  else pct = 0.05 + (subtotal - 2000) / 8000 * 0.10;
  const rawFinal = subtotal - subtotal * pct;
  return { pct, finalPrice: Math.round(rawFinal / 100) * 100 };
}

function periodWord(mode, doubled) {
  if (mode === 'career') return 'Career';
  return doubled ? 'Annual (×2)' : 'Annual';
}

// Mirrors server/src/routes/huddler.js planDefaults() / computePlanTerms() —
// keep in lockstep so the client preview matches what the backend persists.
function planDefaults(mode, mult) {
  if (mode === 'career') return { pct: 0.25, installmentCount: 23 };
  if (mode === 'year' && mult === 2) return { pct: 0.25, installmentCount: 11 };
  return { pct: 0.50, installmentCount: 5 };
}
function computePlanTerms(totalCents, depositSeedCents, count) {
  const installmentCents = Math.round((totalCents - depositSeedCents) / count);
  const depositCents = totalCents - installmentCents * count;
  return { depositCents, installmentCents };
}

// Loose email check — must be of form X@X.X. Not strict (no RFC compliance),
// just enough to reject "asdf" or "name" (no @ or .). Used by the pre-confirm
// form, the title bar EditableField, and the dark Recruit/Parent cards.
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(v) { return !!(v && EMAIL_RX.test(String(v).trim())); }

// =============================================================================
// Main
// =============================================================================
export default function Huddler({ invoiceMode = false }) {
  const [me, setMe] = useState(null);
  const [services, setServices] = useState([]);
  const [byCategory, setByCategory] = useState({});

  const [selections, setSelections] = useState({});
  const [activeCategory, setActiveCategory] = useState(ADDON_CATEGORIES[0]);
  const [pricingMode, setPricingMode] = useState('year');
  const [doubled, setDoubled] = useState(false);
  const [manualOverride, setManualOverride] = useState(null);

  const [paymentPlan, setPaymentPlan] = useState(false);
  const [planTouched, setPlanTouched] = useState(false);
  const [depositDollars, setDepositDollars] = useState(0);
  const [installmentCount, setInstallmentCount] = useState(1);

  // Arrears (invoice mode only, D13): the discreet "manual" schedule reachable
  // from the plan-terms panel footer. When on, submit sends arrears instead of
  // a payment plan. firstAskDollars is the staff-set first payment.
  const [arrearsManual, setArrearsManual] = useState(false);
  const [firstAskDollars, setFirstAskDollars] = useState(0);
  const [askTouched, setAskTouched] = useState(false);

  const [recruit, setRecruit] = useState({
    recruit_first_name: '', recruit_last_name: '', recruit_email: '', recruit_phone: '',
    parent_first_name: '', parent_last_name: '', parent_email: '', parent_phone: '',
  });

  const [confirmed, setConfirmed] = useState(false);
  // S50 recruit gate: Confirm Info resolves the recruit server-side before the
  // page unlocks. { recruitId, existed } — existed drives the notice strip.
  const [confirming, setConfirming] = useState(false);
  const [resolvedRecruit, setResolvedRecruit] = useState(null);
  // Invoice mode only: optional internal note appended to the recruit's file.
  const [invoiceNote, setInvoiceNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [isSticky, setIsSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setIsSticky(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    api.get('/api/auth/me').then(setMe).catch(e => setError(e.message));
    api.get('/api/services').then(({ services, byCategory }) => {
      setServices(services);
      setByCategory(byCategory);
    }).catch(e => setError(e.message));
  }, []);

  // Invoice mode (§5 / D10) sells a-la-carte -- the foundation is excluded from
  // pricing AND rendering. Nulling it here cascades: foundationPrice -> 0, the
  // Core section doesn't render, the summary lists only selected items. Mirrors
  // the server-side skip in huddler.js.
  const foundation = useMemo(() => {
    if (invoiceMode) return null;
    const list = byCategory.core_recruiting || [];
    return list.find(s => !s.is_bundled_in_core);
  }, [byCategory, invoiceMode]);

  const foundationBundled = useMemo(() => {
    const list = byCategory.core_recruiting || [];
    return list.filter(s => s.is_bundled_in_core);
  }, [byCategory]);

  const addonItems = useMemo(() => {
    const pk = priceKey(pricingMode);
    const items = [];
    for (const [id, stepperQty] of Object.entries(selections)) {
      const svc = services.find(s => s.id === id);
      if (!svc) continue;
      if (foundation && svc.id === foundation.id) continue;
      if (svc.is_bundled_in_core) continue;
      const baseQty = isLockable(svc) ? 1 : getBaseQty(svc, pricingMode);
      const displayQty = baseQty * stepperQty;
      items.push({
        svc, stepperQty, baseQty, displayQty,
        lineTotal: careerLineTotal(svc, pricingMode, stepperQty),
      });
    }
    return items;
  }, [services, selections, foundation, pricingMode]);

  const foundationPrice = foundation ? Number(foundation[priceKey(pricingMode)] || 0) : 0;
  const addonsSubtotal = addonItems.reduce((s, it) => s + it.lineTotal, 0);
  const subtotal = foundationPrice + addonsSubtotal;
  // Path B auto-discount is OFF in invoice mode (D9 reversal): an invoice total
  // is a straight sum of line items. The total-only manual override still wins
  // below. Huddler's normal (non-invoice) path is byte-for-byte unchanged.
  const pathB = useMemo(
    () => (invoiceMode ? { pct: 0, finalPrice: subtotal } : pathBDiscount(subtotal)),
    [subtotal, invoiceMode]
  );
  const oneYearFinal = manualOverride != null ? manualOverride : pathB.finalPrice;
  const multiplier = (pricingMode === 'year' && doubled) ? 2 : 1;
  const total = oneYearFinal * multiplier;

  const selectionsKey = JSON.stringify(selections);
  useEffect(() => { setManualOverride(null); }, [selectionsKey, pricingMode, doubled]);

  // Plan-terms defaults: reseed deposit $ + installment count from
  // planDefaults(mode, mult) whenever mode/mult/total changes, until the
  // Scout manually edits a field (planTouched). After touch, mode/mult
  // changes no longer overwrite the Scout's entered terms.
  useEffect(() => {
    if (planTouched) return;
    const { pct, installmentCount: cnt } = planDefaults(pricingMode, multiplier);
    setDepositDollars(Math.round(total * pct));
    setInstallmentCount(cnt);
  }, [pricingMode, multiplier, total, planTouched]);

  function setDepositPct(pct) {
    setPlanTouched(true);
    const clampedPct = Math.max(1, Math.min(90, pct));
    setDepositDollars(Math.round(total * clampedPct / 100));
  }
  function setDepositDollarsManual(dollars) {
    setPlanTouched(true);
    const max = Math.max(1, Math.floor(total) - 1);
    setDepositDollars(Math.max(1, Math.min(max, Math.round(dollars))));
  }
  function setInstallmentCountManual(count) {
    setPlanTouched(true);
    setInstallmentCount(Math.max(1, Math.round(count)));
  }

  // First-ask default: half the invoice total until the Scout edits it. The ask
  // may equal the full total (unlike a plan deposit, which must be < total).
  useEffect(() => {
    if (askTouched) return;
    setFirstAskDollars(Math.max(1, Math.round(total / 2)));
  }, [total, askTouched]);

  function setFirstAskManual(dollars) {
    setAskTouched(true);
    const max = Math.max(1, Math.floor(total));
    setFirstAskDollars(Math.max(1, Math.min(max, Math.round(dollars))));
  }

  function toggleService(svc) {
    setSelections(prev => {
      const next = { ...prev };
      if (next[svc.id]) delete next[svc.id];
      else next[svc.id] = (pricingMode === 'career' && svc.career_tier1_qty != null) ? Number(svc.career_tier1_qty) : 1;
      return next;
    });
  }
  function setStepperQty(svcId, q) {
    const svc = services.find(s => s.id === svcId);
    const cap = svc ? getMaxQty(svc, pricingMode) : null;
    const floor = (pricingMode === 'career' && svc && svc.career_tier1_qty != null) ? Number(svc.career_tier1_qty) : 1;
    let next = Math.max(floor, q);
    if (cap != null) next = Math.min(cap, next);
    setSelections(prev => ({ ...prev, [svcId]: next }));
  }
  function updateRecruit(field, value) {
    setRecruit(prev => ({ ...prev, [field]: value }));
  }
  function applyManualOverride(dollars) {
    setManualOverride(Math.max(1, Math.round(dollars)));
  }

  // Use the module-level isValidEmail helper (also used by RecruitCard,
  // ParentCard, and EditableField for consistent validation).
  const canConfirm = !!(
    recruit.recruit_first_name.trim() &&
    recruit.recruit_last_name.trim() &&
    isValidEmail(recruit.recruit_email) &&
    recruit.parent_first_name.trim() &&
    recruit.parent_last_name.trim() &&
    isValidEmail(recruit.parent_email)
  );

  // S50 recruit gate: Confirm Info is a server round-trip now. Failure keeps
  // the page locked (unconfirmed) with the error surfaced below the form.
  // The server re-resolves by email at /build regardless, so a post-Confirm
  // email edit can't dodge the gate — this call is the UX-facing half.
  async function onConfirm() {
    setError(null);
    setConfirming(true);
    try {
      const res = await api.post('/api/huddler/resolve-recruit', { ...recruit });
      setResolvedRecruit({ recruitId: res.recruit_id, existed: res.existed });
      setConfirmed(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirming(false);
    }
  }

  async function onSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const service_selections = Object.entries(selections).map(([service_id, qty]) => ({
        service_id, qty,
      }));
      const body = {
        ...recruit,
        service_selections,
        pricing_mode: pricingMode,
        multiplier,
        manual_override_cents: manualOverride != null ? Math.round(manualOverride * 100) : null,
      };
      // For reference only — the server re-resolves by email regardless; no
      // client logic may depend on this id.
      if (resolvedRecruit) body.recruit_id = resolvedRecruit.recruitId;
      if (invoiceMode) {
        body.is_invoice = true;
        if (invoiceNote.trim()) body.invoice_note = invoiceNote.trim();
      }
      if (invoiceMode && paymentPlan && arrearsManual) {
        // Manual (arrears) schedule -- staff-set first payment, NOT a plan.
        body.arrears = { first_ask_amount: firstAskDollars };
      } else if (paymentPlan) {
        body.is_payment_plan = true;
        body.plan_overrides = {
          deposit_amount: depositDollars,
          installment_count: installmentCount,
        };
      }
      const res = await api.post('/api/huddler/build', body);
      setResult(res);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = '/login';
  }

  function reset() {
    setResult(null);
    setSelections({});
    setActiveCategory(ADDON_CATEGORIES[0]);
    setPricingMode('year');
    setDoubled(false);
    setManualOverride(null);
    setPaymentPlan(false);
    setPlanTouched(false);
    setDepositDollars(0);
    setInstallmentCount(1);
    setArrearsManual(false);
    setFirstAskDollars(0);
    setAskTouched(false);
    setConfirmed(false);
    setConfirming(false);
    setResolvedRecruit(null);
    setInvoiceNote('');
    setRecruit({
      recruit_first_name: '', recruit_last_name: '', recruit_email: '', recruit_phone: '',
      parent_first_name: '', parent_last_name: '', parent_email: '', parent_phone: '',
    });
  }

  const isReduced = manualOverride != null;
  const periodLbl = periodWord(pricingMode, doubled);

  return (
    <div className="min-h-screen bg-silver-a relative">
      <header className="bg-hunter-deep text-white relative z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/circle-c-wordmark.png"
              alt="CCF"
              className="h-14 w-auto"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div>
              <div className="font-display font-black text-xl leading-none">{invoiceMode ? 'Create Invoice' : 'The Huddler'}</div>
              <div className="text-mid-green text-[0.65rem] tracking-[0.25em] uppercase font-bold mt-1">
                {invoiceMode ? 'Invoice Builder' : 'Individual Recruiting Plan Builder'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <NotificationBell />
            {me && (
              <span className="text-silver-a">
                {me.team.full_name} <span className="text-mid-green">· {me.team.role}</span>
              </span>
            )}
            <Link to="/" className="text-silver-a hover:text-white underline">
              ← Front Office
            </Link>
            <button onClick={signOut} className="text-silver-a hover:text-white underline">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 relative z-10">
        {result ? (
          <SuccessPanel result={result} onReset={reset} invoiceMode={invoiceMode} />
        ) : (
          <form id="huddler-form" onSubmit={onSubmit} className="space-y-5">

            {!confirmed && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <RecruitCard recruit={recruit} update={updateRecruit} />
                  <ParentCard recruit={recruit} update={updateRecruit} />
                </div>
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={!canConfirm || confirming}
                    className="btn-primary text-base px-10 py-3 border border-white"
                    style={{ minWidth: '280px' }}
                  >
                    {confirming ? 'Confirming…' : 'Confirm Info'}
                  </button>
                </div>
              </>
            )}

            {confirmed && (
              <CompressedInfoBar
                recruit={recruit}
                update={updateRecruit}
                isSticky={isSticky}
                periodLbl={periodLbl}
                isReduced={isReduced}
                total={total}
                invoiceMode={invoiceMode}
              />
            )}

            {/* S50: persistent informational notice when the gate matched an
                existing profile — mid-green family, deliberately NOT error-red. */}
            {confirmed && resolvedRecruit?.existed && (
              <div className="bg-green-50 border border-mid-green rounded-md px-4 py-2 text-sm font-bold text-hunter flex items-center gap-2">
                <span className="text-mid-green" aria-hidden="true">✓</span>
                Existing recruit found — proceeding on their file.
              </div>
            )}

            <div
              className={`relative transition-opacity duration-300 ${
                confirmed ? 'opacity-100' : 'opacity-40 pointer-events-none select-none'
              }`}
              aria-hidden={!confirmed}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <PlanBuilderColumn
                  byCategory={byCategory}
                  activeCategory={activeCategory}
                  onSelectCategory={setActiveCategory}
                  selections={selections}
                  onToggleService={toggleService}
                  onSetStepperQty={setStepperQty}
                  pricingMode={pricingMode}
                />

                <PlanSummaryColumn
                  foundation={foundation}
                  foundationPrice={foundationPrice}
                  foundationBundled={foundationBundled}
                  addonItems={addonItems}
                  addonsSubtotal={addonsSubtotal}
                  total={total}
                  isReduced={isReduced}
                  pricingMode={pricingMode}
                  setPricingMode={setPricingMode}
                  doubled={doubled}
                  setDoubled={setDoubled}
                  onApplyManualOverride={applyManualOverride}
                  hasOverride={manualOverride != null}
                  submitting={submitting}
                  paymentPlan={paymentPlan}
                  setPaymentPlan={setPaymentPlan}
                  depositDollars={depositDollars}
                  installmentCount={installmentCount}
                  onSetDepositPct={setDepositPct}
                  onSetDepositDollars={setDepositDollarsManual}
                  onSetInstallmentCount={setInstallmentCountManual}
                  invoiceMode={invoiceMode}
                  arrearsManual={arrearsManual}
                  setArrearsManual={setArrearsManual}
                  firstAskDollars={firstAskDollars}
                  onSetFirstAsk={setFirstAskManual}
                  invoiceNote={invoiceNote}
                  setInvoiceNote={setInvoiceNote}
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}
          </form>
        )}
      </main>
    </div>
  );
}

// =============================================================================
// CompressedInfoBar
// =============================================================================
function CompressedInfoBar({ recruit, update, isSticky, periodLbl, isReduced, total, invoiceMode = false }) {
  const recruitEmailInvalid = !!recruit.recruit_email && !isValidEmail(recruit.recruit_email);
  const parentEmailInvalid  = !!recruit.parent_email  && !isValidEmail(recruit.parent_email);
  const anyEmailInvalid = recruitEmailInvalid || parentEmailInvalid;

  return (
    <div className="sticky top-0 z-40 bg-hunter rounded-md shadow-lg border border-mid-green overflow-hidden">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 py-3">
        <div className="min-w-0">
          <EditableField
            value={`${recruit.recruit_first_name} ${recruit.recruit_last_name}`.trim() || 'Recruit Name'}
            onChange={(v) => {
              const parts = v.trim().split(/\s+/);
              update('recruit_first_name', parts[0] || '');
              update('recruit_last_name', parts.slice(1).join(' ') || '');
            }}
            className="font-display font-black text-white text-lg leading-tight truncate block"
          />
          <div className="text-silver-a text-sm flex gap-2 mt-0.5 min-w-0">
            <EditableField
              value={recruit.recruit_email || 'email'}
              onChange={(v) => update('recruit_email', v)}
              className="truncate min-w-0"
              type="email"
            />
            <span className="text-mid-green">·</span>
            <EditableField
              value={recruit.recruit_phone || 'phone'}
              onChange={(v) => update('recruit_phone', v)}
              className="truncate min-w-0"
              type="tel"
            />
          </div>
        </div>

        <div className="text-center whitespace-nowrap leading-tight">
          {/* Headline: italic mid-green name (silver border for pop) + tracked-uppercase descriptor */}
          <div className="font-display font-black flex items-baseline justify-center gap-2">
            <span className="italic text-mid-green text-xl border border-silver-a/60 rounded px-2 py-0.5">
              {recruit.recruit_first_name || 'Recruit'}'s
            </span>
            <span className="uppercase tracking-wide text-white text-base">
              {invoiceMode ? 'CCF Invoice' : 'Custom Recruiting Plan'}
            </span>
          </div>
          {/* Footer: small tracked label, em-dash flourishes, dollar pill */}
          <div className="mt-1.5 flex items-baseline justify-center gap-2 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-silver-a">
            <span aria-hidden="true" className="text-mid-green text-sm leading-none">—</span>
            <span>{periodLbl} Total{isReduced ? ' · Reduced' : ''}</span>
            <span className="text-mid-green">·</span>
            <span className="bg-white text-mid-green px-2 py-0.5 rounded text-xs tracking-normal normal-case font-display font-black">
              ${total.toLocaleString()}
            </span>
            <span aria-hidden="true" className="text-mid-green text-sm leading-none">—</span>
          </div>
        </div>

        <div className="min-w-0 text-right">
          <EditableField
            value={`${recruit.parent_first_name} ${recruit.parent_last_name}`.trim() || 'Parent Name'}
            onChange={(v) => {
              const parts = v.trim().split(/\s+/);
              update('parent_first_name', parts[0] || '');
              update('parent_last_name', parts.slice(1).join(' ') || '');
            }}
            className="font-display font-black text-white text-lg leading-tight truncate ml-auto block"
            align="right"
          />
          <div className="text-silver-a text-sm flex gap-2 mt-0.5 justify-end min-w-0">
            <EditableField
              value={recruit.parent_phone || 'phone'}
              onChange={(v) => update('parent_phone', v)}
              className="truncate min-w-0"
              type="tel"
              align="right"
            />
            <span className="text-mid-green">·</span>
            <EditableField
              value={recruit.parent_email || 'email'}
              onChange={(v) => update('parent_email', v)}
              className="truncate min-w-0"
              type="email"
              align="right"
            />
          </div>
        </div>
      </div>
      {anyEmailInvalid && (
        <div className="bg-red-900/90 text-red-100 text-xs px-5 py-1.5 border-t border-red-700 flex items-center justify-center gap-3 font-bold">
          <span aria-hidden="true">⚠</span>
          {recruitEmailInvalid && <span>Recruit email is invalid.</span>}
          {recruitEmailInvalid && parentEmailInvalid && <span className="opacity-50">·</span>}
          {parentEmailInvalid  && <span>Parent email is invalid.</span>}
        </div>
      )}
    </div>
  );
}

function EditableField({ value, onChange, className = '', type = 'text', align = 'left' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  function commit() {
    if (draft !== value) onChange(draft);
    setEditing(false);
  }

  // Per-field email validity (used to drive in-place visual feedback)
  const showInvalid  = type === 'email' && !!value && !isValidEmail(value);
  const draftInvalid = type === 'email' && !!draft && !isValidEmail(draft);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter')  { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(value); setEditing(false); }
        }}
        className={`bg-hunter-deep text-white px-1 py-0 rounded outline-none border ${
          draftInvalid ? 'border-red-400' : 'border-mid-green'
        } ${className}`}
        style={{ textAlign: align }}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-text ${showInvalid ? 'underline decoration-wavy decoration-red-400 underline-offset-2' : ''} ${className}`}
      title={showInvalid ? 'Invalid email — click to edit' : 'Click to edit'}
    >
      {value}
    </span>
  );
}

// =============================================================================
// PlanBuilderColumn — transparent wrapper at tallest height + transitions
// =============================================================================
function PlanBuilderColumn({
  byCategory, activeCategory, onSelectCategory,
  selections, onToggleService, onSetStepperQty, pricingMode,
}) {
  const tabs = ADDON_CATEGORIES.map(cat => {
    const items = (byCategory[cat] || []).filter(s => !s.is_bundled_in_core);
    const selectedCount = items.filter(s => selections[s.id]).length;
    return { cat, label: CATEGORY_LABELS[cat], items, selectedCount };
  }).filter(t => t.items.length > 0);

  const active = tabs.find(t => t.cat === activeCategory) || tabs[0];

  // Measure tallest category for outer wrapper height
  const measureRef = useRef(null);
  const [wrapperMinH, setWrapperMinH] = useState(0);
  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const children = Array.from(measureRef.current.children);
    let max = 0;
    for (const c of children) {
      const h = c.getBoundingClientRect().height;
      if (h > max) max = h;
    }
    if (max > 0) setWrapperMinH(Math.ceil(max) + 1);
  }, [byCategory, pricingMode]);

  // Header bar wipe + cascade animation key
  const [animKey, setAnimKey] = useState(0);
  const lastCatRef = useRef(active && active.cat);
  useEffect(() => {
    if (active && active.cat !== lastCatRef.current) {
      lastCatRef.current = active.cat;
      setAnimKey(k => k + 1);
    }
  }, [active]);

  return (
    <div className="space-y-3">
      <div className="flex items-center px-1">
        <h2 className="font-display font-black text-2xl text-hunter">Plan Builder</h2>
      </div>

      {/* 3×2 tab grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 bg-white rounded-md shadow-sm border border-gray-200 p-1">
        {tabs.map(t => {
          const isActive = active && t.cat === active.cat;
          return (
            <button
              key={t.cat}
              type="button"
              onClick={() => onSelectCategory(t.cat)}
              className={`w-full flex items-center justify-center gap-1 px-2 py-2 rounded font-display font-bold text-sm transition-colors ${
                isActive ? 'bg-hunter-deep text-white' : 'bg-transparent text-hunter hover:bg-silver-a'
              }`}
            >
              <span className="truncate">{t.label}</span>
              {/* Always-rendered badge slot — invisible when 0 to keep tab height constant */}
              <span
                className={`text-[0.65rem] font-black rounded-full px-1 py-0.5 bg-mid-green text-white shrink-0 ${
                  t.selectedCount > 0 ? '' : 'invisible'
                }`}
              >
                {t.selectedCount > 0 ? t.selectedCount : '0'}
              </span>
            </button>
          );
        })}
      </div>

      {/* Transparent wrapper sized to tallest category, inner card at natural height */}
      <div style={{ minHeight: wrapperMinH ? `${wrapperMinH}px` : undefined }}>
        <div
          key={animKey}
          className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden animate-stageEnter"
        >
          {/* Header bar with wipe animation */}
          <div className="relative bg-mid-green text-white font-display font-black px-5 py-3 overflow-hidden">
            <span className="relative z-10">{active ? active.label : ''}</span>
            <span aria-hidden="true" className="absolute inset-0 bg-hunter-deep animate-headerWipe" />
          </div>

          {/* Service cards — cascade fade-in via per-child animation delay */}
          <div className="p-4 grid grid-cols-1 gap-3">
            {active && active.items.map((svc, i) => (
              <div
                key={svc.id}
                className="animate-cardCascade opacity-0"
                style={{ animationDelay: `${120 + i * 50}ms` }}
              >
                <ServiceRow
                  svc={svc}
                  checked={!!selections[svc.id]}
                  stepperQty={selections[svc.id] || 1}
                  pricingMode={pricingMode}
                  onToggle={() => onToggleService(svc)}
                  onSetStepperQty={(q) => onSetStepperQty(svc.id, q)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Travel disclaimer — only renders when active category contains a
          requires_travel service. Bolted directly under the white stage card. */}
      {active && active.items.some(s => s.requires_travel) && (
        <p className="text-sm text-mid-green font-bold italic px-2 -mt-1">
          * Travel-required services: plus airfare, hotel, $100/day food.
        </p>
      )}

      {/* Hidden measurer */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-99999px', top: 0, width: 0, height: 0, overflow: 'hidden' }}
      >
        {tabs.map(t => (
          <div key={'measure-' + t.cat} style={{ width: '480px' }}>
            <div className="bg-white rounded-md border border-gray-200">
              <div className="bg-mid-green text-white font-display font-black px-5 py-3">{t.label}</div>
              <div className="p-4 grid grid-cols-1 gap-3">
                {t.items.map(svc => (
                  <ServiceRow
                    key={svc.id}
                    svc={svc}
                    checked={false}
                    stepperQty={1}
                    pricingMode={pricingMode}
                    onToggle={() => {}}
                    onSetStepperQty={() => {}}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// PlanSummaryColumn
// =============================================================================
function PlanSummaryColumn({
  foundation, foundationPrice, foundationBundled,
  addonItems, addonsSubtotal, total, isReduced,
  pricingMode, setPricingMode, doubled, setDoubled,
  onApplyManualOverride, hasOverride, submitting,
  paymentPlan, setPaymentPlan,
  depositDollars, installmentCount, onSetDepositPct, onSetDepositDollars, onSetInstallmentCount,
  invoiceMode = false, arrearsManual = false, setArrearsManual, firstAskDollars = 0, onSetFirstAsk,
  invoiceNote = '', setInvoiceNote,
}) {
  const [pulseKey, setPulseKey] = useState(0);
  const lastTotalRef = useRef(total);
  useEffect(() => {
    if (lastTotalRef.current !== total) {
      lastTotalRef.current = total;
      setPulseKey(k => k + 1);
    }
  }, [total]);

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);
  useEffect(() => {
    if (editing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editing]);

  function startEdit() {
    setEditValue(String(total));
    setEditing(true);
  }
  function commitEdit() {
    const n = parseInt(editValue.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(n) && n > 0) {
      const mult = (pricingMode === 'year' && doubled) ? 2 : 1;
      onApplyManualOverride(n / mult);
    }
    setEditing(false);
  }

  // Invoice mode: the floating tag reads a plain "Custom Total:" -- no period
  // word (invoices are always 1-year ×1) and no "(reduced)" (Path B is off, so
  // an invoice override isn't a discount off an auto-reduced price). Huddler's
  // normal path keeps its exact prior label, byte-for-byte.
  const totalLabel = invoiceMode
    ? 'Custom Total'
    : `Custom ${periodWord(pricingMode, doubled)} Total${hasOverride ? ' (reduced)' : ''}`;

  return (
    <div className="space-y-3">
      <div className="flex items-center px-1">
        <h2 className="font-display font-black text-2xl text-hunter">Current Plan Summary</h2>
      </div>

      <div className="relative">
        {/* Floating Custom Total tag with white pill on the price */}
        <div
          key={pulseKey}
          className="absolute -top-3 right-6 bg-mid-green text-white text-xs font-display font-black uppercase tracking-widest px-4 py-2 rounded shadow-lg z-10 animate-pulseOnce"
        >
          {editing ? (
            <span className="flex items-center gap-1">
              {totalLabel}:&nbsp;
              <input
                ref={editInputRef}
                type="text"
                inputMode="numeric"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={e => {
                  if (e.key === 'Enter')  { e.preventDefault(); commitEdit(); }
                  if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
                }}
                className="bg-white text-mid-green px-1 py-0.5 rounded w-24 text-base outline-none"
              />
            </span>
          ) : (
            <button type="button" onClick={startEdit} className="cursor-text" title="Click to set a custom price">
              {totalLabel}:&nbsp;
              <span className="bg-white text-mid-green px-2 py-0.5 rounded text-base">
                ${total.toLocaleString()}
              </span>
            </button>
          )}
        </div>

        <div className="card-dark relative overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.05]">
            <img
              src="/circle-c-hero.png"
              alt=""
              className="max-w-[60%] max-h-[80%] object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>

          <div className="relative z-10 space-y-4">
            {/* Three-way pricing toggle — hidden in invoice mode (§5 / D9 pins
                1-year, ×1; the toggle and its divider go away entirely). */}
            {!invoiceMode && (
              <>
                <div className="flex justify-end">
                  <ThreeWayPricingToggle
                    pricingMode={pricingMode}
                    doubled={doubled}
                    setPricingMode={setPricingMode}
                    setDoubled={setDoubled}
                  />
                </div>
                <div className="h-px bg-hunter" />
              </>
            )}

            {/* Core Recruiting Services */}
            {foundation && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display font-black text-white text-base uppercase tracking-wider">
                    Core Recruiting Services
                  </h3>
                  <span className="bg-white text-mid-green px-2 py-0.5 rounded font-display font-black">
                    ${foundationPrice.toLocaleString()}
                  </span>
                </div>

                {foundationBundled.length > 0 && (
                  <ul className="grid grid-cols-1 gap-y-1 text-base text-silver-a">
                    {foundationBundled.map(s => (
                      <li key={s.id} className="flex items-baseline gap-2">
                        <span className="text-mid-green">·</span>
                        <span className="font-bold text-white">{s.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {foundation && <div className="h-px bg-hunter" />}

            {/* Selected services (invoice mode drops the "Huddler" branding) */}
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display font-black text-white text-base uppercase tracking-wider">
                  {invoiceMode ? 'Invoice Services' : 'Huddler Recruiting Services'}
                </h3>
                <span
                  className="bg-white text-mid-green px-2 py-0.5 rounded font-display font-black"
                  title="Pre-discount subtotal"
                >
                  ${addonsSubtotal.toLocaleString()}
                </span>
              </div>

              {addonItems.length === 0 ? (
                <p className="text-silver-a text-base italic">No custom services selected.</p>
              ) : (
                <ul className="grid grid-cols-1 gap-y-1.5 text-base text-silver-a">
                  {addonItems.map((it, i) => {
                    const lockable = isLockable(it.svc);
                    const showBadge = !lockable;
                    return (
                      <li key={i} className="flex items-baseline justify-between gap-2">
                        <span className="flex-1 min-w-0 flex items-baseline gap-2">
                          <span className="text-mid-green">·</span>
                          <span className="font-bold text-white truncate">{it.svc.name}</span>
                          {showBadge && (
                            <span className="text-mid-green font-display font-black shrink-0">
                              ×{it.displayQty}
                            </span>
                          )}
                        </span>
                        <span className="font-display font-black text-mid-green whitespace-nowrap">
                          ${it.lineTotal.toLocaleString()}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="h-px bg-hunter" />

            {/* Bottom row: Full Amount/Payments switch left, Send button right */}
            <div className="flex items-center justify-between gap-3">
              <PaymentPlanToggle on={paymentPlan} onChange={setPaymentPlan} />
              <button
                type="submit"
                form="huddler-form"
                disabled={submitting}
                className="btn-primary text-sm px-5 py-2.5 uppercase tracking-widest border border-white whitespace-nowrap"
              >
                {submitting ? 'Sending…' : (invoiceMode ? 'Send Invoice' : 'Send Checkout Link')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={paymentPlan ? '' : 'invisible pointer-events-none'}
        aria-hidden={!paymentPlan}
      >
        <PlanTermsPanel
          total={total}
          depositDollars={depositDollars}
          installmentCount={installmentCount}
          onSetDepositPct={onSetDepositPct}
          onSetDepositDollars={onSetDepositDollars}
          onSetInstallmentCount={onSetInstallmentCount}
          invoiceMode={invoiceMode}
          arrearsManual={arrearsManual}
          setArrearsManual={setArrearsManual}
          firstAskDollars={firstAskDollars}
          onSetFirstAsk={onSetFirstAsk}
        />
      </div>

      {/* S50: invoice-only Notes — appended to the recruit's file on Send.
          Sits below the PlanTermsPanel slot (which holds its space even when
          hidden), so nothing above it moves. Absent entirely in Huddler mode. */}
      {invoiceMode && (
        <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-mid-green text-white font-display font-black px-5 py-3">
            Notes
          </div>
          <div className="p-4">
            <textarea
              value={invoiceNote}
              onChange={e => setInvoiceNote(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Optional internal note — saved to the recruit's file when the invoice is sent."
              className="w-full bg-white text-black text-sm leading-snug px-3 py-2 rounded border border-gray-200 outline-none focus:border-mid-green resize-y"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Toggles
// =============================================================================
function ThreeWayPricingToggle({ pricingMode, doubled, setPricingMode, setDoubled }) {
  const opts = [
    { key: 'year',    label: 'Annual',      mode: 'year',   dbl: false },
    { key: 'year-x2', label: 'Annual (×2)', mode: 'year',   dbl: true },
    { key: 'career',  label: 'Career',      mode: 'career', dbl: false },
  ];
  const activeKey = pricingMode === 'career' ? 'career' : (doubled ? 'year-x2' : 'year');
  return (
    <div className="inline-flex bg-hunter rounded-md p-1 border border-hunter">
      {opts.map(o => {
        const active = activeKey === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => { setPricingMode(o.mode); setDoubled(o.dbl); }}
            className={`px-3 py-1.5 text-xs font-display font-black uppercase tracking-widest rounded transition-colors whitespace-nowrap ${
              active ? 'bg-mid-green text-white shadow' : 'text-silver-a hover:text-white'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function PaymentPlanToggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`px-3 py-1.5 text-xs font-display font-black uppercase tracking-widest rounded border transition-colors ${
        on ? 'bg-mid-green text-white border-mid-green' : 'bg-hunter text-silver-a border-hunter hover:text-white'
      }`}
      title="Toggle payment plan (deposit + monthly installments)"
    >
      {on ? 'Payments' : 'Full Amount'}
    </button>
  );
}

// =============================================================================
// PlanTermsPanel — compact, half-width payment-plan terms, rendered directly
// under the Current Plan Summary card when paymentPlan is on. Colorway
// mirrors the Plan Builder service-listing card (green header, white body)
// rather than the dark cards. Deposit % and Deposit $ are two views of one
// number (depositDollars is the state of record; % is derived for display).
// Resolved preview mirrors the backend's remainder-absorbing computePlanTerms().
// =============================================================================
function PlanTermsPanel({
  total, depositDollars, installmentCount, onSetDepositPct, onSetDepositDollars, onSetInstallmentCount,
  invoiceMode = false, arrearsManual = false, setArrearsManual, firstAskDollars = 0, onSetFirstAsk,
}) {
  const displayPct = total > 0 ? Math.max(1, Math.min(90, Math.round((depositDollars / total) * 100))) : 0;

  const totalCents = Math.round(total * 100);
  const depositSeedCents = Math.round(depositDollars * 100);
  const count = Math.max(1, installmentCount);
  const { depositCents, installmentCents } = computePlanTerms(totalCents, depositSeedCents, count);

  return (
    <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-mid-green text-white font-display font-black px-5 py-3">
        {arrearsManual ? 'Payment Schedule' : 'Payment Plan Terms'}
      </div>

      <div className="p-4 space-y-3">
        {arrearsManual ? (
          <>
            {/* Single staff-set first payment. The three plan fields collapse to
                one; nothing on screen reads as arrears / balance / debt (D13). */}
            <div className="grid grid-cols-3 gap-3">
              <PlanTermsField
                label="First payment $"
                value={Math.round(firstAskDollars)}
                prefix="$"
                min={1}
                max={Math.max(1, Math.floor(total))}
                onCommit={onSetFirstAsk}
              />
            </div>
            <p className="text-black text-sm leading-snug">
              First payment{' '}
              <span className="text-mid-green font-display font-black">
                ${Math.round(firstAskDollars).toLocaleString()}
              </span>{' '}
              today; remaining balance billed as requested.
            </p>
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <PlanTermsField
                label="Deposit %"
                value={displayPct}
                suffix="%"
                min={1}
                max={90}
                onCommit={onSetDepositPct}
              />
              <PlanTermsField
                label="Deposit $"
                value={Math.round(depositDollars)}
                prefix="$"
                min={1}
                max={Math.max(1, Math.floor(total) - 1)}
                onCommit={onSetDepositDollars}
              />
              <PlanTermsField
                label="# Payments"
                value={installmentCount}
                min={1}
                onCommit={onSetInstallmentCount}
              />
            </div>

            <p className="text-black text-sm leading-snug">
              Deposit{' '}
              <span className="text-mid-green font-display font-black">
                ${(depositCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>{' '}
              today, then{' '}
              <span className="text-mid-green font-display font-black">
                ${(installmentCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {' '}×{' '}
              <span className="text-mid-green font-display font-black">
                {count}
              </span>{' '}
              monthly.
            </p>
          </>
        )}

        {/* Discreet arrears affordance (D13) — invoice mode only, panel footer,
            styled as scheduling minutiae: lowercase, muted, no border. */}
        {invoiceMode && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setArrearsManual(!arrearsManual)}
              className="text-xs lowercase text-silver-mute hover:text-hunter transition-colors"
              title="Switch payment schedule mode"
            >
              {arrearsManual ? 'standard plan' : 'manual'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Click-to-edit numeric field — same interaction pattern as the Custom Total
// tag in PlanSummaryColumn (click -> input focuses+selects -> Enter/blur
// commits, Escape reverts). Restyled for the panel's white body.
function PlanTermsField({ label, value, prefix = '', suffix = '', min, max, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function startEdit() {
    setDraft(String(value));
    setEditing(true);
  }
  function commit() {
    const n = parseInt(String(draft).replace(/[^0-9-]/g, ''), 10);
    if (!isNaN(n)) {
      let clamped = n;
      if (min != null) clamped = Math.max(min, clamped);
      if (max != null) clamped = Math.min(max, clamped);
      onCommit(clamped);
    }
    setEditing(false);
  }

  return (
    <div>
      <div className="text-black text-[0.65rem] font-bold tracking-[0.2em] uppercase mb-1">{label}</div>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter')  { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { e.preventDefault(); setDraft(String(value)); setEditing(false); }
          }}
          className="bg-white text-mid-green px-2 py-1 rounded border border-mid-green outline-none w-full text-lg font-display font-black"
        />
      ) : (
        <button type="button" onClick={startEdit} className="cursor-text text-left w-full" title="Click to edit">
          <span className="block bg-white text-mid-green px-2 py-1 rounded border border-gray-200 text-lg font-display font-black">
            {prefix}{value.toLocaleString()}{suffix}
          </span>
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Recruit & Parent dark cards (pre-confirm)
// =============================================================================
function RecruitCard({ recruit, update }) {
  const [emailTouched, setEmailTouched] = useState(false);
  const showEmailErr = emailTouched && !!recruit.recruit_email && !isValidEmail(recruit.recruit_email);
  return (
    <div className="card-dark">
      <h2 className="font-display font-black text-xl text-white mb-1">Recruit Info</h2>
      <div className="h-0.5 w-12 bg-mid-green mb-3" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-dark mb-1">First name *</label>
          <input required className="input-dark" value={recruit.recruit_first_name}
            onChange={e => update('recruit_first_name', e.target.value)} />
        </div>
        <div>
          <label className="label-dark mb-1">Last name *</label>
          <input required className="input-dark" value={recruit.recruit_last_name}
            onChange={e => update('recruit_last_name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label-dark mb-1">Email *</label>
          <input
            type="email"
            required
            className={`input-dark ${showEmailErr ? 'border-red-400' : ''}`}
            value={recruit.recruit_email}
            onChange={e => update('recruit_email', e.target.value)}
            onBlur={() => setEmailTouched(true)}
          />
          {showEmailErr && (
            <p className="text-red-300 text-xs mt-1 font-bold">
              ⚠ That email doesn&apos;t look right. Try again.
            </p>
          )}
        </div>
        <div className="col-span-2">
          <label className="label-dark mb-1">Phone</label>
          <input className="input-dark" value={recruit.recruit_phone}
            onChange={e => update('recruit_phone', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

function ParentCard({ recruit, update }) {
  const [emailTouched, setEmailTouched] = useState(false);
  const showEmailErr = emailTouched && !!recruit.parent_email && !isValidEmail(recruit.parent_email);
  return (
    <div className="card-dark">
      <h2 className="font-display font-black text-xl text-white mb-1">Parent / Guardian</h2>
      <div className="h-0.5 w-12 bg-mid-green mb-3" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-dark mb-1">First name *</label>
          <input required className="input-dark" value={recruit.parent_first_name}
            onChange={e => update('parent_first_name', e.target.value)} />
        </div>
        <div>
          <label className="label-dark mb-1">Last name *</label>
          <input required className="input-dark" value={recruit.parent_last_name}
            onChange={e => update('parent_last_name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label-dark mb-1">Email *</label>
          <input
            type="email"
            required
            className={`input-dark ${showEmailErr ? 'border-red-400' : ''}`}
            value={recruit.parent_email}
            onChange={e => update('parent_email', e.target.value)}
            onBlur={() => setEmailTouched(true)}
          />
          {showEmailErr && (
            <p className="text-red-300 text-xs mt-1 font-bold">
              ⚠ That email doesn&apos;t look right. Try again.
            </p>
          )}
        </div>
        <div className="col-span-2">
          <label className="label-dark mb-1">Phone</label>
          <input className="input-dark" value={recruit.parent_phone}
            onChange={e => update('parent_phone', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ServiceRow
// =============================================================================
function ServiceRow({ svc, checked, stepperQty, pricingMode, onToggle, onSetStepperQty }) {
  const lockable = isLockable(svc);
  const baseQty = lockable ? 1 : getBaseQty(svc, pricingMode);
  const cap = getMaxQty(svc, pricingMode);
  const minQty = (pricingMode === 'career' && svc.career_tier1_qty != null) ? Number(svc.career_tier1_qty) : 1;
  const displayQty = baseQty * (checked ? stepperQty : minQty);
  const lineTotal = careerLineTotal(svc, pricingMode, checked ? stepperQty : minQty);
  const showBadge = !lockable;
  const showStepperArea = !lockable && cap != null;
  const atCap = cap != null && stepperQty >= cap;

  return (
    <label
      className={`flex items-start gap-2 p-2.5 border rounded-md cursor-pointer transition-colors ${
        checked ? 'border-mid-green bg-green-50' : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 accent-mid-green" />
      <div className="flex-1 min-w-0">
        {/* Top row: title (truncate) + badge + price all baseline-aligned, price right at the edge */}
        <div className="flex items-baseline gap-2 leading-tight">
          <span className="font-bold text-black truncate flex-1 min-w-0">
            {svc.name}
            {svc.requires_travel && <span className="text-mid-green">*</span>}
          </span>
          {showBadge && (
            <span className="text-black font-display font-black shrink-0">
              ×{displayQty}
            </span>
          )}
          <span className="font-display font-black text-mid-green shrink-0">
            ${lineTotal.toLocaleString()}
          </span>
        </div>

        {/* Description spans the full content width — no longer constrained by stepper-column width */}
        {svc.description && (
          <div className="text-sm text-black/80 leading-snug line-clamp-2 mt-0.5">
            {svc.description}
          </div>
        )}

        {/* Stepper row — only renders when the service supports a stepper. Slot
            stays mounted (invisible when unchecked) so card height doesn't bounce. */}
        {showStepperArea && (
          <div className={`mt-1 flex justify-end ${checked ? '' : 'invisible'}`}>
            <QtyStepper value={stepperQty} onChange={onSetStepperQty} atCap={atCap} minQty={minQty} />
          </div>
        )}
      </div>
    </label>
  );
}

function QtyStepper({ value, onChange, atCap, minQty = 1 }) {
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
  return (
    <span
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      className="inline-flex items-center gap-1 mt-0.5"
    >
      <button type="button" onClick={stop(() => onChange(Math.max(minQty, value - 1)))}
        disabled={value <= minQty}
        className="w-6 h-6 rounded bg-silver-a hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center text-hunter font-black text-sm"
        aria-label="Decrease quantity">−</button>
      <span className="w-6 text-center font-display font-black text-hunter">{value}</span>
      <button type="button" onClick={stop(() => onChange(value + 1))}
        disabled={atCap}
        className="w-6 h-6 rounded bg-mid-green hover:bg-hunter disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white font-black text-sm"
        aria-label="Increase quantity"
        title={atCap ? 'Maximum reached' : ''}>+</button>
    </span>
  );
}

function SuccessPanel({ result, onReset, invoiceMode = false }) {
  const [showStatus, setShowStatus] = useState(false);
  return (
    <div className="card max-w-2xl mx-auto mt-8">
      <h2 className="text-2xl font-display font-bold text-hunter mb-3">{invoiceMode ? '✓ Invoice built' : '✓ Plan built'}</h2>
      <p className="text-silver-mute mb-5">
        {invoiceMode ? 'Invoice created.' : 'Pending enrollment created.'}{' '}
        {result.email_sent
          ? (invoiceMode ? 'Invoice emailed to recruit.' : 'Checkout link emailed to recruit.')
          : <span className="text-red-700">Email failed: {result.email_error}</span>}
      </p>

      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-widest text-silver-mute mb-2">
          Checkout URL
        </div>
        <div className="font-mono text-sm break-all bg-silver-a px-3 py-2 rounded border border-gray-200">
          {result.checkout_url}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <a href={result.checkout_url} target="_blank" rel="noopener noreferrer" className="btn-secondary">
          Open checkout
        </a>
        <button type="button" onClick={() => setShowStatus(v => !v)} className="btn-primary">
          {showStatus ? 'Hide Status' : 'Status'}
        </button>
      </div>

      {showStatus && result.pending_enrollment && (
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-silver-mute mb-2">
            Checkout Status
          </div>
          <CheckoutStatusTracker pendingId={result.pending_enrollment.id} />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CheckoutStatusTracker — Sent → Opened → Clicked → Completed
// =============================================================================
function CheckoutStatusTracker({ pendingId }) {
  const [row, setRow] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchRow() {
      try {
        const data = await api.get(`/api/huddler/pending/${pendingId}/status`);
        if (!cancelled) setRow(data);
      } catch (e) {
        // Silent fail — tracker just stays at "loading"
      }
    }
    fetchRow();

    // Realtime subscription
    let channel;
    try {
      channel = supabase
        .channel(`pending-status-${pendingId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'pending_enrollments',
            filter: `id=eq.${pendingId}`,
          },
          (payload) => {
            if (payload && payload.new && !cancelled) setRow(payload.new);
          }
        )
        .subscribe();
    } catch (e) {}

    // Poll every 5s as a safety net
    const poll = setInterval(fetchRow, 5000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (channel) {
        try { supabase.removeChannel(channel); } catch (e) {}
      }
    };
  }, [pendingId]);

  // 3 stages — Opened removed because Apple Mail Privacy Protection
  // (~50% of email traffic) auto-fetches tracking pixels on delivery,
  // making "opened" indistinguishable from "delivered." See S9.7 thread.
  //
  // Payment-plan carts get a different 3-stage array — Card Saved / Deposit
  // Sent / Deposit Paid. A-1 only ever sets card_saved_at; deposit_invoiced_at
  // and deposit_paid_at are written by A-2's webhook, so a plan row here will
  // only ever show step 1 lit — that's correct in-progress state, not a bug.
  //
  // Discriminator (Build B §5): gate on plan_terms.is_plan, NOT bare plan_terms
  // presence. Arrears rows carry plan_terms={arrears:true} WITHOUT is_plan, so
  // they must fall through to the standard Sent / Clicked / Completed stages
  // (Completed = balance cleared, stamped by the arrears webhook branch).
  const stages = row && row.plan_terms && row.plan_terms.is_plan
    ? [
        { key: 'card_saved_at',       label: 'Card Saved' },
        { key: 'deposit_invoiced_at', label: 'Deposit Sent' },
        { key: 'deposit_paid_at',     label: 'Deposit Paid' },
      ]
    : [
        { key: 'sent_at',      label: 'Sent' },
        { key: 'clicked_at',   label: 'Clicked' },
        { key: 'completed_at', label: 'Completed' },
      ];

  return (
    <div className="bg-silver-a rounded p-4">
      <div className="flex items-center gap-1">
        {stages.map((stage, i) => {
          const active = !!(row && row[stage.key]);
          const isLast = i === stages.length - 1;
          return (
            <div key={stage.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black transition-colors ${
                    active ? 'bg-mid-green text-white' : 'bg-white text-silver-mute border border-gray-300'
                  }`}
                >
                  {active ? '✓' : i + 1}
                </div>
                <div className={`text-[0.7rem] font-bold uppercase tracking-wider mt-1 ${
                  active ? 'text-hunter' : 'text-silver-mute'
                }`}>
                  {stage.label}
                </div>
              </div>
              {!isLast && (
                <div className={`h-1 flex-1 mx-1 -mt-5 ${
                  active && row && row[stages[i + 1].key] ? 'bg-mid-green' : 'bg-gray-300'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
